import { FormProvider } from 'react-hook-form'
import { DialogContent, DialogHeader } from '@/view/components/ui/dialog'
import useOffersModal from './useOffersModal'
import { Button } from '@/view/components/ui/button'
import { Input } from '@/view/components/ui/input'
import { Label } from '@/view/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/view/components/ui/select'
import {
  addMoneySimbols,
  addMoneySimbolsCents,
} from '@/app/utils/formatMoneyValue'
import { Skeleton } from '@/view/components/ui/skeleton'
import { Copy } from 'lucide-react'
import Header from '../PersonalInfoModal/components/Header'
import { StepProgress } from '../components/StepProgress'

interface OffersModalProps {
  onContinue: () => void
  onBack: () => void
}

export default function OffersModal({ onContinue, onBack }: OffersModalProps) {
  const {
    form,
    submitModal,
    isPending,
    isFetching,
    installments,
    selectedInstallment,
    setSelectedInstallment,
    borrowedValue,
    commission,
    matricula,
    digitoMatricula,
    manualRegistration,
    setManualRegistration,
    manualRegistrationDigit,
    setManualRegistrationDigit,
    netIncome,
    setNetIncome,
    hasRenda,
    isNetIncomeIncomplete,
    linkConsentimento,
    handleCopyConsentLink,
    errorMessage,
    parcelas,
    requiresConsent,
    authorizedConsent,
    isConsentAuthorized,
    consentLoading,
    handleVerifyConsent,
    steps,
    isError,
    retry,
    cancel,
  } = useOffersModal({
    onContinue,
  })

  const handleSelectInstallment = (idSimulacao: string) => {
    const installment = installments.find((i) => i.idSimulacao === idSimulacao)
    if (installment) {
      setSelectedInstallment(installment)
    }
  }

  return (
    <DialogContent
      className="flex max-h-[90vh] w-[95vw] max-w-[500px] flex-col overflow-y-auto md:max-w-[500px]"
      closeOnClickOutside={!isFetching}
    >
      <DialogHeader>
        <Header
          title="Opções de crédito"
          onBack={() => {
            cancel()
            onBack()
          }}
        />
      </DialogHeader>

      {errorMessage || isError ? (
        <div className="flex flex-col items-center justify-center gap-6 py-10">
          <p className="text-center text-base font-semibold text-black01">
            {errorMessage ?? 'Não foi possível realizar a simulação.'}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                cancel()
                onBack()
              }}
              type="button"
              className="w-[140px] rounded-full text-primary"
            >
              Voltar
            </Button>
            <Button
              onClick={() => retry()}
              type="button"
              className="w-[140px] rounded-full"
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submitModal} className="flex flex-1 flex-col">
          <FormProvider {...form}>
            <div>
              {isFetching && (
                <StepProgress
                  steps={steps}
                  title="Simulando na Crefisa"
                  className="mb-4"
                />
              )}
              <div>
                <Label className="text-sm font-medium text-gray-500">
                  Valor solicitado
                </Label>
                <Input
                  className="mt-2 text-xl"
                  value={
                    selectedInstallment
                      ? addMoneySimbols(borrowedValue)
                      : 'Selecione uma parcela'
                  }
                  disabled
                />
              </div>

              <div className="mt-4">
                {isFetching ? (
                  <Skeleton className="h-10 w-full rounded-md" />
                ) : installments.length > 0 && selectedInstallment ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-xs font-medium text-black01 md:text-sm">
                      Valor da parcela:{' '}
                      <span className="whitespace-nowrap font-bold">
                        {addMoneySimbols(parcelas)}
                      </span>
                    </p>
                    <div className="w-[140px]">
                      <Select
                        value={selectedInstallment.idSimulacao}
                        onValueChange={handleSelectInstallment}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {installments.map((i) => (
                            <SelectItem
                              key={i.idSimulacao}
                              value={i.idSimulacao}
                            >
                              {i.quantidadeParcelas}x
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm font-medium">
                    Nenhuma parcela disponível
                  </p>
                )}
                <Label className="text-sm font-medium text-gray-500">
                  Renda Líquida
                </Label>
                <Input
                  className="mt-2"
                  placeholder="R$ 0,00"
                  inputMode="numeric"
                  maxLength={14}
                  value={addMoneySimbolsCents(netIncome)}
                  disabled={hasRenda}
                  onChange={(e) =>
                    setNetIncome(e.target.value.replace(/\D/g, ''))
                  }
                />
              </div>

              {typeof commission === 'number' && borrowedValue > 0 && (
                <p className="mt-2 text-sm font-medium text-black01">
                  Comissão ({commission}%):{' '}
                  <span className="font-bold">
                    {addMoneySimbols((commission / 100) * borrowedValue)}
                  </span>
                </p>
              )}

              {(!matricula || !digitoMatricula) && (
                <div className="mt-4 flex gap-3">
                  <Label className="w-[70%] text-sm font-medium">
                    NIS
                    <Input
                      className="mt-2"
                      placeholder="NIS"
                      maxLength={10}
                      value={manualRegistration}
                      onChange={(e) =>
                        setManualRegistration(e.target.value.replace(/\D/g, ''))
                      }
                    />
                  </Label>
                  <Label className="w-[30%] text-sm font-medium">
                    Dígito
                    <Input
                      className="mt-2"
                      placeholder="Dígito"
                      maxLength={1}
                      value={manualRegistrationDigit}
                      onChange={(e) =>
                        setManualRegistrationDigit(
                          e.target.value.replace(/\D/g, ''),
                        )
                      }
                    />
                  </Label>
                </div>
              )}

              {requiresConsent && (
                <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-black01">
                    É obrigatório realizar o consentimento para finalizar a
                    proposta deste cliente.
                  </p>
                </div>
              )}

              {isConsentAuthorized && authorizedConsent ? (
                <div className="mt-4 rounded-lg border border-green-300 bg-green-50 p-3">
                  <p className="text-sm font-semibold text-black01">
                    Consentimento autorizado pelo cliente
                  </p>
                  <p className="mt-1 text-sm font-medium text-black04">
                    {`${authorizedConsent.nomeBanco} · Ag. ${authorizedConsent.agencia} · Conta ${authorizedConsent.conta}-${authorizedConsent.digitoConta}`}
                  </p>
                </div>
              ) : (
                (!!linkConsentimento || requiresConsent) && (
                  <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <p className="text-sm font-semibold text-black01">
                      Esse cliente precisa fazer o consentimento:
                    </p>
                    {!!linkConsentimento && (
                      <button
                        type="button"
                        onClick={handleCopyConsentLink}
                        className="mt-2 flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                      >
                        <Copy className="h-4 w-4" />
                        Copiar link do consentimento
                      </button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleVerifyConsent}
                      isLoading={consentLoading}
                      disabled={consentLoading}
                      className="mt-3 w-full rounded-full text-primary"
                    >
                      Verificar consentimento
                    </Button>
                  </div>
                )
              )}

              <div className="mt-6 flex justify-end gap-2">
                <Button
                  disabled={
                    isPending ||
                    !selectedInstallment ||
                    installments?.length === 0 ||
                    isNetIncomeIncomplete ||
                    (requiresConsent && !isConsentAuthorized)
                  }
                  type="submit"
                  className="w-[160px] rounded-full bg-primary font-semibold text-white"
                >
                  Avançar
                </Button>
              </div>
            </div>
          </FormProvider>
        </form>
      )}
    </DialogContent>
  )
}
